import { useEffect, useState, useMemo, useCallback } from "react";
import { api, apiErrorMessage, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Vote, CheckCircle2, Clock, Users, Award, FileDown, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * R157C — Resident-facing elections portal.
 * Any authenticated community member (resident/owner/board/PM/admin) can
 *   • see elections for their community
 *   • nominate themselves or a neighbor while nominations are open
 *   • cast a secret ballot while voting is open (one-time, receipt-tracked)
 *   • see the certified result + download the certificate PDF
 */
const STATUS = {
  nominations_open: { label: "Nominations open", cls: "border-amber-200 bg-amber-50 text-amber-800", icon: Users },
  nominations_closed: { label: "Nominations closed", cls: "border-zinc-200 bg-zinc-50 text-zinc-700", icon: Clock },
  voting_open: { label: "Voting open", cls: "border-orange-200 bg-orange-50 text-orange-800", icon: Vote },
  voting_closed: { label: "Voting closed", cls: "border-zinc-200 bg-zinc-50 text-zinc-700", icon: Clock },
  certified: { label: "Certified", cls: "border-emerald-200 bg-emerald-50 text-emerald-800", icon: Award },
};

export default function ResidentElectionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nominateDialog, setNominateDialog] = useState(null);   // election object
  const [voteDialog, setVoteDialog] = useState(null);           // election object
  const [busy, setBusy] = useState(false);
  const [nominee, setNominee] = useState("");
  const [nomineeBio, setNomineeBio] = useState("");
  const [neighbors, setNeighbors] = useState([]);
  const [ballot, setBallot] = useState([]);   // list of nomination_id
  const [confirmVote, setConfirmVote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/board/elections");
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load elections"); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

    // Neighbors for nomination — pulled from a public member directory endpoint
  useEffect(() => {
    if (!nominateDialog) return;
    (async () => {
      try {
        const { data } = await api.get("/community/members");
        setNeighbors(data.items || []);
      } catch (e) { console.debug("community members fetch failed (optional)", e); }
    })();
  }, [nominateDialog]);

  const openNominate = (e) => {
    setNominee(user?.id || "");         // default self-nom
    setNomineeBio("");
    setNominateDialog(e);
  };
  const openVote = (e) => {
    setBallot([]);
    setConfirmVote(false);
    setVoteDialog(e);
  };

  const submitNomination = async () => {
    if (!nominee) return toast.error("Choose a nominee");
    setBusy(true);
    try {
      await api.post(`/board/elections/${nominateDialog.id}/nominations`, {
        nominee_user_id: nominee,
        nominee_bio: nomineeBio || null,
      });
      toast.success(nominee === user?.id
        ? "You're on the ballot — an admin will accept your nomination shortly."
        : "Nomination submitted.");
      setNominateDialog(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Nomination failed"); }
    setBusy(false);
  };

  const toggleBallot = (nid) => {
    setBallot((prev) => {
      if (prev.includes(nid)) return prev.filter((x) => x !== nid);
      if (prev.length >= (voteDialog?.seats_available || 1)) {
        toast.error(`You can vote for at most ${voteDialog.seats_available} candidates`);
        return prev;
      }
      return [...prev, nid];
    });
  };

  const submitBallot = async () => {
    if (ballot.length === 0) return toast.error("Pick at least one candidate");
    setBusy(true);
    try {
      const { data } = await api.post(`/board/elections/${voteDialog.id}/ballot`,
        { choices: ballot });
      toast.success("Your secret ballot is recorded. Thanks for voting!");
      // Save a local receipt so the confirmation stays visible after reload
      try {
        const receipts = JSON.parse(localStorage.getItem("pmp:election_receipts") || "{}");
        receipts[voteDialog.id] = { ballot_hash: data.ballot_hash, at: new Date().toISOString() };
        localStorage.setItem("pmp:election_receipts", JSON.stringify(receipts));
      } catch (e) { console.debug("election receipt cache failed", e); }
      setVoteDialog(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Vote failed"); }
    setBusy(false);
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading elections…</div>;
  }

  return (
    <div data-testid="resident-elections-page">
      <PageHeader
        eyebrow="Governance"
        title="Board Elections"
        subtitle="Nominate a neighbor, run yourself, or cast your secret ballot. Every vote is hash-committed — nobody can link your identity to how you voted."
      />

      {items.length === 0 ? (
        <Card className="p-8 text-center bg-white">
          <Vote className="h-8 w-8 mx-auto text-zinc-400 mb-3" />
          <div className="text-sm text-zinc-600">
            There aren't any active elections for your community right now.
            We'll email you the moment nominations open.
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((e) => (
            <ElectionCard key={e.id}
                          election={e}
                          user={user}
                          onNominate={() => openNominate(e)}
                          onVote={() => openVote(e)}
            />
          ))}
        </div>
      )}

      {/* Nominate dialog */}
      <Dialog open={!!nominateDialog} onOpenChange={(o) => !o && setNominateDialog(null)}>
        <DialogContent className="bg-white text-zinc-900 sm:max-w-[520px]"
                       data-testid="nominate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-orange-500" /> Submit a nomination
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Every accepted candidate appears on the secret ballot once voting opens.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nominee</Label>
              <Select value={nominee} onValueChange={setNominee}>
                <SelectTrigger data-testid="nominee-select" className="mt-1">
                  <SelectValue placeholder="Choose a resident…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user?.id || "self"}>
                    <b>{user?.name} (nominate yourself)</b>
                  </SelectItem>
                  {neighbors.filter((n) => n.id !== user?.id).map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.name} {n.unit ? `· Unit ${n.unit}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Short bio (optional)</Label>
              <Textarea rows={4} value={nomineeBio}
                        onChange={(e) => setNomineeBio(e.target.value)}
                        className="mt-1 text-sm"
                        placeholder="Their qualifications, priorities, and why they should serve on the board."
                        data-testid="nominee-bio" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNominateDialog(null)}>Cancel</Button>
            <Button onClick={submitNomination} disabled={busy || !nominee}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="submit-nomination">
              {busy ? "Submitting…" : "Submit nomination"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vote dialog */}
      <Dialog open={!!voteDialog} onOpenChange={(o) => !o && setVoteDialog(null)}>
        <DialogContent className="bg-white text-zinc-900 sm:max-w-[560px] max-h-[90vh] overflow-y-auto"
                       data-testid="vote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Cast your secret ballot
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Pick up to <b>{voteDialog?.seats_available}</b> candidate{voteDialog?.seats_available === 1 ? "" : "s"}. Your identity is
              never linked to your choices — we store a cryptographic hash of your vote, not your name.
            </DialogDescription>
          </DialogHeader>
          {voteDialog && !confirmVote && (
            <div className="space-y-2">
              {(voteDialog.nominations || []).filter((n) => n.accepted === true).map((n) => (
                <label key={n.id}
                       className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition ${
                         ballot.includes(n.id)
                           ? "border-orange-500 bg-orange-50"
                           : "border-zinc-200 hover:bg-zinc-50"
                       }`}
                       data-testid={`vote-choice-${n.id}`}>
                  <input type="checkbox" checked={ballot.includes(n.id)}
                          onChange={() => toggleBallot(n.id)}
                          className="mt-1 accent-orange-500"
                          data-testid={`vote-checkbox-${n.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{n.nominee_name}</div>
                    {n.nominee_bio && (
                      <div className="text-xs text-zinc-500 mt-1 line-clamp-3">{n.nominee_bio}</div>
                    )}
                  </div>
                </label>
              ))}
              {(voteDialog.nominations || []).filter((n) => n.accepted === true).length === 0 && (
                <div className="text-sm text-zinc-500 text-center py-6">
                  No accepted candidates on the ballot yet. Check back when the admin has reviewed nominations.
                </div>
              )}
            </div>
          )}
          {voteDialog && confirmVote && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
              <div className="font-medium text-amber-900 mb-2">Confirm your ballot</div>
              <div className="text-xs text-amber-800 mb-2">
                You're about to vote for <b>{ballot.length}</b> candidate{ballot.length === 1 ? "" : "s"}:
              </div>
              <ul className="text-xs text-amber-800 list-disc pl-5">
                {ballot.map((bid) => {
                  const nom = voteDialog.nominations.find((n) => n.id === bid);
                  return <li key={bid}>{nom?.nominee_name}</li>;
                })}
              </ul>
              <div className="text-[11px] text-amber-700 mt-2">
                Ballots are final — you can't change your vote after submitting.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoteDialog(null)}>Cancel</Button>
            {!confirmVote ? (
              <Button onClick={() => setConfirmVote(true)}
                       disabled={ballot.length === 0}
                       className="bg-orange-500 hover:bg-orange-600 text-white"
                       data-testid="review-ballot-btn">
                Review my ballot
              </Button>
            ) : (
              <Button onClick={submitBallot} disabled={busy}
                       className="bg-emerald-600 hover:bg-emerald-700 text-white"
                       data-testid="submit-ballot-btn">
                {busy ? "Submitting…" : "Submit final ballot"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ElectionCard({ election, user, onNominate, onVote }) {
  const st = STATUS[election.status] || { label: election.status, cls: "border-zinc-200 bg-zinc-50" };
  const Icon = st.icon || Vote;
  const canNominate = election.status === "nominations_open";
  const canVote = election.status === "voting_open" && !election.my_voted;
  const alreadyVoted = election.my_voted;
  const tally = election.tally || {};
  const winners = (election.certified_result?.winners) || [];

  const daysTo = useMemo(() => {
    const target = election.status === "nominations_open"
      ? election.nominations_close_at
      : election.status === "voting_open"
        ? election.voting_close_at
        : null;
    if (!target) return null;
    const diffMs = new Date(target).getTime() - Date.now();
    if (diffMs <= 0) return "closes now";
    const days = Math.floor(diffMs / 86400e3);
    const hours = Math.floor((diffMs % 86400e3) / 3600e3);
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  }, [election]);

  return (
    <Card className="p-5 bg-white" data-testid={`election-card-${election.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge variant="outline" className={`text-[10px] ${st.cls}`}>
              <Icon className="h-3 w-3 mr-1" /> {st.label}
            </Badge>
            {daysTo && <Badge variant="outline" className="text-[10px] border-zinc-200 bg-zinc-50 text-zinc-700">
              <Clock className="h-3 w-3 mr-1" /> {daysTo}
            </Badge>}
            {alreadyVoted && (
              <Badge variant="outline" className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-800">
                <CheckCircle2 className="h-3 w-3 mr-1" /> You've voted
              </Badge>
            )}
          </div>
          <div className="text-lg font-semibold font-display">{election.title}</div>
          {election.description && (
            <div className="text-xs text-zinc-500 mt-0.5">{election.description}</div>
          )}
          <div className="text-xs text-zinc-500 mt-1">
            {election.seats_available} seat{election.seats_available === 1 ? "" : "s"} available
          </div>
        </div>
        <div className="shrink-0 flex gap-2 flex-wrap justify-end">
          {canNominate && (
            <Button onClick={onNominate} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"
                    data-testid={`nominate-btn-${election.id}`}>
              <Users className="h-4 w-4 mr-1" /> Nominate
            </Button>
          )}
          {canVote && (
            <Button onClick={onVote} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid={`vote-btn-${election.id}`}>
              <Vote className="h-4 w-4 mr-1" /> Cast my ballot
            </Button>
          )}
          {election.status === "certified" && (
            <a href={`${API}/admin/board/elections/${election.id}/certificate.pdf`}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 h-9 hover:bg-emerald-100"
               data-testid={`cert-pdf-${election.id}`}>
              <FileDown className="h-3 w-3" /> Certificate PDF
            </a>
          )}
        </div>
      </div>

      {/* Nominations preview */}
      {(election.nominations || []).length > 0 && (
        <div className="border-t border-zinc-100 pt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            {election.status === "nominations_open" ? "Nominations in review" : "Candidates"}
            {" "}({(election.nominations || []).filter((n) => n.accepted === true || (election.status === "nominations_open" && n.accepted === null)).length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(election.nominations || [])
              .filter((n) => election.status === "nominations_open" ? true : n.accepted === true)
              .map((n) => {
                const isWinner = winners.find((w) => w.nomination_id === n.id);
                return (
                  <div key={n.id} className={`rounded-md border p-2 ${
                    isWinner ? "border-emerald-200 bg-emerald-50" : "border-zinc-100 bg-zinc-50"
                  }`}>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm truncate">{n.nominee_name}</div>
                      {isWinner && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800">
                        <Award className="h-3 w-3 mr-1" /> Elected
                      </Badge>}
                      {n.accepted === null && (
                        <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-800">Pending</Badge>
                      )}
                      {n.accepted === false && (
                        <Badge variant="outline" className="text-[10px] border-rose-200 text-rose-800">Not accepted</Badge>
                      )}
                    </div>
                    {n.nominee_bio && (
                      <div className="text-[11px] text-zinc-600 mt-1 line-clamp-2">{n.nominee_bio}</div>
                    )}
                    {election.status === "certified" && tally[n.id] !== undefined && (
                      <div className="text-[11px] text-zinc-500 mt-1">
                        <b>{tally[n.id]}</b> vote{tally[n.id] === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {election.status === "certified" && election.certified_result && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-emerald-600 shrink-0" />
          <div className="text-emerald-800">
            <b>{election.certified_result.ballots_total} ballot{election.certified_result.ballots_total === 1 ? "" : "s"}</b> cast · {election.certified_result.winners.length} new board member{election.certified_result.winners.length === 1 ? "" : "s"} elected. Thanks for participating!
          </div>
        </div>
      )}
    </Card>
  );
}
